/**
 * The app frame: a top bar whose left side is "what are we working on and how are
 * the machines" — the open release (a tab home to the actions) and a tab per host
 * with a status dot — and whose right side is the release archive, the debug
 * snapshot, and the operator's identity. The bar warns loudly when the UI is
 * pointed at the emulator instead of a live project, because operating the wrong
 * plane is the mistake this whole tool exists to prevent. The bug icon opens the
 * debug snapshot (`DebugDialog`) — the state the page sees, to paste when it is
 * wrong.
 */
import { useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    AppBar,
    Box,
    Button,
    Chip,
    Container,
    Menu,
    MenuItem,
    Stack,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material';
import ArrowDropDownRounded from '@mui/icons-material/ArrowDropDownRounded';
import FiberManualRecord from '@mui/icons-material/FiberManualRecord';
import type { HostRole } from '@finvari/deployer';
import { useOperator, signOutOperator, type Operator } from '../auth';
import { CAPTION_TICK_MS, useHost, useNow, useReleasePosition } from '../hooks';
import { headerBadge } from '../lib/headerBadge';
import { hostView, PRESENTATION_COLOR, PRESENTATION_LABEL } from '../lib/hostStatus';
import { BirdLogo } from './BirdLogo';
import { DebugButton } from './DebugDialog';

const IS_EMULATOR = import.meta.env.VITE_USE_EMULATOR === 'true';

/** The two hosts, each its own tab so the operator picks the machine deliberately. */
const HOST_TABS: Array<{ role: HostRole; label: string }> = [
    { role: 'prepare', label: 'Prepare VM' },
    { role: 'release', label: 'Release VM' },
];

export function Layout({ children }: { children: ReactNode }) {
    return (
        <Box>
            <AppBar position="static" color="default" elevation={1}>
                <Toolbar>
                    <Stack
                        component={RouterLink}
                        to="/"
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ textDecoration: 'none', color: 'inherit' }}
                    >
                        <BirdLogo size={28} />
                        <Typography variant="h6">Release Status</Typography>
                    </Stack>
                    {/*
                      * The open release sits first, as a tab: it is the primary thing
                      * being worked on and the way back to the actions that drive it.
                      */}
                    <PositionTab />
                    {HOST_TABS.map((tab) => (
                        <HostNavTab key={tab.role} role={tab.role} label={tab.label} />
                    ))}
                    <Box sx={{ flexGrow: 1 }} />
                    <Button component={RouterLink} to="/releases" color="inherit" sx={{ mr: 1 }}>
                        Release History
                    </Button>
                    {/*
                      * Only the emulator warning earns a permanent chip. In a live
                      * project the id ("finvari-release") is the expected case, and a
                      * chip an operator learns to stop reading is worse than no chip —
                      * so the plane is called out only when it is the dangerous one.
                      */}
                    {IS_EMULATOR && <Chip size="small" color="warning" label="emulator" sx={{ mr: 1 }} />}
                    <DebugButton />
                    <OperatorMenu />
                </Toolbar>
            </AppBar>
            <Container maxWidth="lg" sx={{ py: 3 }}>
                {children}
            </Container>
        </Box>
    );
}

/** The dot colour beside a host tab, from the shared presentation-to-severity map. */
const DOT_COLOR: Record<'success' | 'warning' | 'default' | 'error', string> = {
    success: 'success.main',
    warning: 'warning.main',
    error: 'error.main',
    default: 'text.disabled',
};

/**
 * A host tab with a live status dot, so an operator sees at a glance whether the
 * prepare and release machines are up without opening either page.
 */
function HostNavTab({ role, label }: { role: HostRole; label: string }) {
    const { value: doc, loading } = useHost(role);
    const now = useNow(CAPTION_TICK_MS);
    const view = hostView(doc, now, loading);
    return (
        <Tooltip title={PRESENTATION_LABEL[view.presentation]}>
            <Button
                component={RouterLink}
                to={`/hosts/${role}`}
                color="inherit"
                startIcon={<FiberManualRecord sx={{ fontSize: 12, color: DOT_COLOR[PRESENTATION_COLOR[view.presentation]] }} />}
            >
                {label}
            </Button>
        </Tooltip>
    );
}

/**
 * Which release is open and how far the deploy has got, rendered as the leftmost
 * tab and linking home.
 *
 * The deploying half is the part of this system an operator leaves running: a
 * compile takes twenty minutes, and they come back to whichever tab was open — the
 * instruction page, a host page, a second window. The tab answers "which release,
 * and how far in" from any page, and is the way back to the actions.
 */
function PositionTab() {
    const { phase, version, progress, loading } = useReleasePosition();
    const badge = headerBadge({ phase, version, progress });
    if (loading || !badge.text) return null;
    return (
        <Button
            component={RouterLink}
            to="/"
            color={badge.emphasis ? 'primary' : 'inherit'}
            sx={{ ml: 2, fontWeight: badge.emphasis ? 700 : 500 }}
        >
            {badge.text}
        </Button>
    );
}

/**
 * The operator's name, opening a menu whose one choice is Sign out — the usual
 * place to end a session, and one click further from the accidental logout that a
 * bare "Sign out" button beside every page invites.
 */
function OperatorMenu() {
    const operator = useOperator();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const close = () => setAnchor(null);
    return (
        <>
            <Button
                color="inherit"
                onClick={(e) => setAnchor(e.currentTarget)}
                endIcon={<ArrowDropDownRounded />}
                sx={{ textTransform: 'none' }}
            >
                {operatorLabel(operator)}
            </Button>
            <Menu anchorEl={anchor} open={!!anchor} onClose={close}>
                <MenuItem
                    onClick={() => {
                        close();
                        void signOutOperator();
                    }}
                >
                    Sign out
                </MenuItem>
            </Menu>
        </>
    );
}

/**
 * The operator's display name if the identity carries one; otherwise the local
 * part of their email ("paul.mclachlan"), since the @finvari.com domain is the
 * same for everyone here and only takes up room.
 */
function operatorLabel(op: Operator): string {
    if (op.displayName && op.displayName !== op.email) return op.displayName;
    return op.email.split('@')[0];
}
